import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router";
import { FiSearch } from "react-icons/fi";

import { useFeatureFlagService } from "hooks/useFeatureFlagService";
import { useUserConfig } from "hooks/useUserConfig";
import { RUNTIME_CONFIG } from "helpers/constants/filters";
import ContainerHeaderLayout from "views/components/ContainerHeaderLayout/ContainerHeaderLayout";
import UnavailableModal from "views/components/UnavillableModal/UnavailableModal";
import VLColorfulLoader from "views/uikit/VLColorfulLoader";

import SettingRow, { SettingEntry } from "./SettingRow";
import styles from "./style.module.scss";

const API_BASE = "/api/v1/internal/runtime-config";

interface NonChangeableEntry {
  key: string;
}

interface ListSettingsResponse {
  changeable: SettingEntry[];
  non_changeable: NonChangeableEntry[];
  active_override_count: number;
}

export default function RuntimeConfigPage() {
  const navigate = useNavigate();
  const { refetch: refetchUserConfig } = useUserConfig();
  const isEnabled = useFeatureFlagService(RUNTIME_CONFIG).enabled;

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ListSettingsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      // Use fetch instead of axios to avoid the global 403 auto-logout interceptor
      const res = await fetch(`${API_BASE}/settings`, {
        credentials: "include",
      });
      if (!res.ok) {
        setError(
          res.status === 403 ? "Access denied" : "Failed to load settings",
        );
        return;
      }
      setData(await res.json());
    } catch {
      setError("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isEnabled) {
      refresh();
    } else {
      setLoading(false);
    }
  }, [isEnabled]);

  const handleApply = async (key: string, value: any) => {
    try {
      const res = await fetch(`${API_BASE}/settings/${key}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ value }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        alert(data.detail || "Failed to apply override");
        return;
      }
      await refresh();
      await refetchUserConfig();
    } catch {
      alert("Failed to apply override");
    }
  };

  const handleRevert = async (key: string) => {
    try {
      const res = await fetch(`${API_BASE}/settings/${key}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) {
        alert("Failed to revert setting");
        return;
      }
      await refresh();
      await refetchUserConfig();
    } catch {
      alert("Failed to revert setting");
    }
  };

  const handleRevertAll = async () => {
    try {
      const res = await fetch(`${API_BASE}/settings`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) {
        alert("Failed to revert all");
        return;
      }
      await refresh();
      await refetchUserConfig();
    } catch {
      alert("Failed to revert all");
    }
  };

  const categories = useMemo(() => {
    if (!data) return ["all"];
    const cats = Array.from(new Set(data.changeable.map((s) => s.category)));
    return ["all", ...cats];
  }, [data]);

  const visibleSettings = useMemo(() => {
    if (!data) return [];
    let filtered = data.changeable;
    if (activeCategory !== "all") {
      filtered = filtered.filter((s) => s.category === activeCategory);
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (s) =>
          s.key.toLowerCase().includes(q) ||
          s.description.toLowerCase().includes(q),
      );
    }
    return filtered;
  }, [data, activeCategory, searchQuery]);

  if (!isEnabled) {
    return (
      <UnavailableModal
        onClose={() => navigate("/")}
        actionText="Go back to datasets"
      />
    );
  }

  return (
    <>
      <Helmet>
        <title>Runtime Config</title>
      </Helmet>
      <ContainerHeaderLayout
        upperRow={
          <div className={styles.pageHeader}>
            <div className={styles.title}>
              <div className={styles.pageTitle}>Runtime Config</div>
              <div className={styles.pageSubtitle}>
                In-memory setting overrides — lost on restart.
                {data && data.active_override_count > 0 && (
                  <span className={styles.overrideBadge}>
                    {data.active_override_count} active
                  </span>
                )}
              </div>
            </div>
            {data && data.active_override_count > 0 && (
              <button className={styles.revertBtn} onClick={handleRevertAll}>
                Revert All
              </button>
            )}
          </div>
        }
      />

      {loading ? (
        <VLColorfulLoader />
      ) : error ? (
        <div className={styles.errorMessage}>{error}</div>
      ) : (
        <div className={styles.content}>
          <div className={styles.filtersRow}>
            <div className={styles.categoryTabs}>
              {categories.map((cat) => (
                <button
                  key={cat}
                  className={`${styles.catTab} ${
                    activeCategory === cat ? styles.active : ""
                  }`}
                  onClick={() => setActiveCategory(cat)}
                >
                  {cat === "all" ? "All" : cat}
                </button>
              ))}
            </div>
            <div className={styles.searchWrapper}>
              <span className={styles.searchIcon}>
                <FiSearch size={14} />
              </span>
              <input
                className={styles.searchInput}
                type="text"
                placeholder="Search settings..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          <div className={styles.settingsTable}>
            <div className={styles.tableHeader}>
              <span>Setting</span>
              <span>Description</span>
              <span>Value</span>
              <span>Actions</span>
            </div>
            {visibleSettings.map((s) => (
              <SettingRow
                key={s.key}
                setting={s}
                onToggle={handleApply}
                onRevert={handleRevert}
              />
            ))}
          </div>

          {data && data.non_changeable.length > 0 && (
            <details className={styles.nonChangeable}>
              <summary>
                {data.non_changeable.length} non-changeable settings (values
                hidden)
              </summary>
              <div className={styles.nonChangeableList}>
                {data.non_changeable.map((s) => (
                  <span key={s.key} className={styles.nonChangeableKey}>
                    {s.key}
                  </span>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </>
  );
}
